// Job matching for Bank Statement lines (Priority).
//
// Priority keeps a recurring job as ONE doc and expands its visits at read
// time, so "which job was on this date?" must look at the job's occurrences,
// not just startDate/endDate (a recurring job's endDate is the series end,
// not a visit). Every date here is the occurrence's SCHEDULED day (after any
// "edit this job only" reschedule); the CANONICAL key is what gets saved on
// the bank line as jobOccurrenceDate, same as completedDates/invoicedDates.

import moment from "moment";
import { getOccurrencesForJob } from "./getOccurrencesForJob";
import {
  isRecurring,
  toDateKey,
  getExcludedDateSet,
  getOverrideScheduledKey,
  getOccurrenceSpanDays,
} from "./recurringOccurrenceUtils";

const shiftKey = (key, days) => moment(key, "YYYY-MM-DD").add(days, "days").format("YYYY-MM-DD");

const dayGap = (a, b) => Math.abs(moment(a, "YYYY-MM-DD").diff(moment(b, "YYYY-MM-DD"), "days"));

/**
 * Visits of a job that land within `windowDays` of `dateKey`.
 * Returns [{ canonical, scheduled }] sorted by distance from dateKey.
 * Rescheduled visits (occurrenceOverrides) report their new day; excluded
 * (deleted) visits are skipped.
 */
export const bankJobVisitsNear = (job, dateKey, windowDays = 5) => {
  const day = toDateKey(dateKey);
  if (!job || !day) return [];
  const win = Math.max(0, Number(windowDays) || 0);

  if (!isRecurring(job)) {
    const start = toDateKey(job.startDate || job.date || job.createdAt);
    if (!start) return [];
    const span = getOccurrenceSpanDays(job);
    const end = span ? shiftKey(start, span) : toDateKey(job.endDate) || start;
    const inRange = day >= start && day <= (end >= start ? end : start);
    const gap = inRange ? 0 : Math.min(dayGap(day, start), dayGap(day, end || start));
    if (gap > win) return [];
    return [{ canonical: start, scheduled: start, end: end && end !== start ? end : "", gap }];
  }

  const excluded = getExcludedDateSet(job);
  // Look a little wider for canonical dates because a visit may have been
  // moved by up to a few weeks with "edit this job only".
  const lookStart = shiftKey(day, -(win + 45));
  const lookEnd = shiftKey(day, win + 45);
  return getOccurrencesForJob(job, lookStart, lookEnd)
    .filter((canonical) => !excluded.has(canonical))
    .map((canonical) => {
      const scheduled = getOverrideScheduledKey(job, canonical) || canonical;
      return { canonical, scheduled, end: "", gap: dayGap(day, scheduled) };
    })
    .filter((v) => v.gap <= win)
    .sort((a, b) => a.gap - b.gap);
};

/** Jobs (and the visit) that were on exactly this day. */
export const bankJobsOnDay = (jobs = [], dateKey) =>
  jobs
    .map((job) => {
      const visit = bankJobVisitsNear(job, dateKey, 0)[0];
      return visit ? { job, occurrenceDate: isRecurring(job) ? visit.canonical : "" } : null;
    })
    .filter(Boolean);

export const bankJobSite = (job = {}) => job.siteName || job.propertyName || job.property || "Unknown site";
export const bankJobScope = (job = {}) => job.scope || job.serviceType || job.jobType || "";

/**
 * Job choices for one money-out line: { id, jobId, occurrenceDate, score, label, job }.
 * For a recurring job the option is the visit nearest the line's date, so the
 * line is tied to that visit (jobOccurrenceDate) and not the whole series.
 */
export const bankJobMatchesForDate = (jobs = [], dateKey, windowDays = 5, limit = 12) => {
  const day = toDateKey(dateKey);
  if (!day) return [];
  return jobs
    .map((job) => {
      const visit = bankJobVisitsNear(job, day, windowDays)[0];
      if (!visit) return null;
      const recurring = isRecurring(job);
      const dateText = recurring
        ? `visit ${visit.scheduled}${visit.scheduled !== visit.canonical ? ` (moved from ${visit.canonical})` : ""}`
        : `${visit.scheduled}${visit.end ? ` to ${visit.end}` : ""}`;
      return {
        id: job.id,
        jobId: job.id,
        occurrenceDate: recurring ? visit.canonical : "",
        score: visit.gap,
        label: `${job.wo || "No WO"} • ${bankJobSite(job)} • ${dateText}${recurring ? " • Recurring" : ""} • ${bankJobScope(job)}`,
        job,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.score - b.score)
    .slice(0, limit);
};
